import moment, { Moment } from "moment-timezone";

export default class ApiDateParser {
  static parse(date: string): Moment {
    date = this.denormalize(date);

    return moment(date, "YYYY-MM-DDTHH:mm:ssZ");
  }

  static parseTimestamp(timestamp: number): Moment {
    const date = moment.unix(timestamp);
    const asApiDate = this.toApiDate(date);
    return this.parse(asApiDate);
  }

  static combinedDateEpochParseUTC(date: string, epochOffset: number): Moment {
    const parsedEpoch = moment.unix(epochOffset).utc();
    const formattedEpoch = parsedEpoch.format("HH:mm:ss");
    const utcWithTime = moment.utc(
      `${date}T${formattedEpoch}`,
      "YYYY-MM-DDTHH:mm:ss"
    );
    return utcWithTime;
  }

  static combineDateAndTimeUTC(utcDate: Moment, utcTime: Moment): Moment {
    return moment.utc(
      `${utcDate.format("YYYY-MM-DD")}T${utcTime.format("HH:mm:ss")}Z`,
      "YYYY-MM-DDTHH:mm:ssZ"
    );
  }

  static combinedDateTimeParseUTC(date: string, time: string): Moment {
    const parsedDateUTC = moment.utc(date);
    const parsedTimeUTC = moment.utc(time, "YYYY-MM-DDTHH:mm:ssZ");
    const combinedDate = ApiDateParser.combineDateAndTimeUTC(
      parsedDateUTC,
      parsedTimeUTC
    );
    return combinedDate;
  }

  static combinedDateTimeParseLocal(date: string, time: string): Moment {
    const parsed = this.combinedDateTimeParseUTC(date, time).local();
    return parsed;
  }

  static combinedDateEpochParseLocal(
    date: string,
    epochOffset: number
  ): Moment {
    const parsed = this.combinedDateEpochParseUTC(date, epochOffset).local();
    return parsed;
  }

  static parseDateUTC(date: string): Moment {
    const utcParsed = moment.utc(date, "YYYY-MM-DD").set({
      hours: 0,
      minutes: 0,
      seconds: 0,
      milliseconds: 0,
    });
    return utcParsed;
  }

  static parseDateLocal(date: string): Moment {
    const utcParsed = this.parseDateUTC(date);
    return utcParsed.local();
  }

  static toNormalizedApiDate(date: Moment): string {
    return this.normalize(this.toApiDate(date));
  }
  static toNormalizedZoneDate(date: Moment): string {
    return this.normalize(this.zoneDate(date));
  }
  static toApiDate(date: Moment): string {
    // This is the date format that the API models expect to parse.
    // It does the opposite of the parse method.
    // We use it in case we needed to fake an Api response.
    return moment(date).utc().toISOString();
  }
  static zoneDate(date: Moment): string {
    // This is the date format that the API models expect to parse.
    // It does the opposite of the parse method.
    // We use it in case we needed to fake an Api response.
    const currentUtcOffset = moment().utcOffset();

    return moment(date).utc(!!currentUtcOffset).toISOString();
  }

  static normalize(date: string): string {
    if (typeof date !== "string") return date;
    date = date?.replace(this.yesterday, "1999-12-31");
    date = date?.replace(this.today, "2000-01-01");
    date = date?.replace(this.tomorrow, "2000-01-02");
    return date;
  }

  static get yesterday(): string {
    return moment().subtract(1, "days").format("YYYY-MM-DD");
  }
  static get today(): string {
    return moment().format("YYYY-MM-DD");
  }
  static get tomorrow(): string {
    return moment().add(1, "days").format("YYYY-MM-DD");
  }

  static denormalize(date: string): string {
    if (typeof date !== "string") return date;
    date = date?.replace("1999-12-31", this.yesterday);
    date = date?.replace("2000-01-01", this.today);
    date = date?.replace("2000-01-02", this.tomorrow);
    return date;
  }
}
